import { ResearchPlan } from '@/types/intelligence'
import { useMemo, useState, useRef, useEffect } from 'react'
import { fetchPersonas, PersonaGroup } from '../../lib/intelligenceApi'

const IconX = ({ className }: { className?: string }) => (
  <svg
    className={className}
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth={1.8}
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <line x1="18" y1="6" x2="6" y2="18" />
    <line x1="6" y1="6" x2="18" y2="18" />
  </svg>
)

const IconChevron = ({ className, expanded }: { className?: string; expanded: boolean }) => (
  <svg
    className={`${className} transition-transform duration-200 ${expanded ? 'rotate-180' : ''}`}
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth={2}
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <path d="M6 9l6 6 6-6" />
  </svg>
)

const IconSparkles = ({ className }: { className?: string }) => (
  <svg
    className={className}
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth={1.8}
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <path d="M12 3l1.8 4.8L18.6 9.6l-4.8 1.8L12 16.2l-1.8-4.8L5.4 9.6l4.8-1.8L12 3z" />
    <path d="M19 4l.7 1.8L21.5 6.5l-1.8.7L19 9l-.7-1.8-1.8-.7 1.8-.7L19 4z" />
    <path d="M5 15l1 2.5L8.5 18 6 19l-1 2.5L4 19l-2.5-1L4 17.5 5 15z" />
  </svg>
)

const IconPencil = ({ className }: { className?: string }) => (
  <svg
    className={className}
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth={1.8}
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <path d="M12 20h9" />
    <path d="M16.5 3.5a2.12 2.12 0 013 3l-10 10-4 1 1-4 10-10z" />
  </svg>
)

type QuestionKey = keyof ResearchPlan['research_questions']

const QUESTION_LABELS: Record<QuestionKey, string> = {
  decision_questions: 'Decision Questions',
  mechanism_questions: 'Mechanism Questions',
  evidence_questions: 'Evidence Targets',
  counterpoint_questions: 'Counterpoints & Risks',
  data_questions: 'Data & Figures',
  scenario_questions: 'Scenario Probes',
}

export {}

interface Props {
  plan?: ResearchPlan
  mode?: 'review' | 'loading'
  query?: string
  onPlanChange?: (plan: ResearchPlan) => void
  onCancel: () => void
  onConfirm?: (updatedPersona?: string) => void
  isConfirming?: boolean
}

export function IntelligencePlanModal({
  plan,
  mode = 'review',
  query,
  onPlanChange,
  onCancel,
  onConfirm,
  isConfirming = false,
}: Props) {
  const [showAdvanced, setShowAdvanced] = useState(false)
  const [showPersonaSelector, setShowPersonaSelector] = useState(false)
  const [personaGroups, setPersonaGroups] = useState<PersonaGroup[]>([])
  const [selectedPersona, setSelectedPersona] = useState(plan?.inputs.persona_mode ?? 'management_consultant')
  const scrollRef = useRef<HTMLDivElement>(null)
  const [loadingProgress, setLoadingProgress] = useState(12)

  useEffect(() => {
    const previousOverflow = document.body.style.overflow
    const previousModalState = document.body.getAttribute('data-modal-open')
    document.body.style.overflow = 'hidden'
    document.body.setAttribute('data-modal-open', 'true')
    window.dispatchEvent(new Event('modal-open-changed'))
    return () => {
      document.body.style.overflow = previousOverflow
      if (previousModalState === null) {
        document.body.removeAttribute('data-modal-open')
      } else {
        document.body.setAttribute('data-modal-open', previousModalState)
      }
      window.dispatchEvent(new Event('modal-open-changed'))
    }
  }, [])

  // Load personas on mount
  useEffect(() => {
    if (mode !== 'review') return
    fetchPersonas().then(setPersonaGroups).catch(console.error)
  }, [mode])

  useEffect(() => {
    if (!plan) return
    setSelectedPersona(plan.inputs.persona_mode)
  }, [plan])

  useEffect(() => {
    if (mode !== 'loading') return

    let elapsed = 0
    const durations = [2600, 2600, 3200, 2600, 3200, 2600, 1800]
    const totalDuration = durations.reduce((sum, duration) => sum + duration, 0)

    const interval = window.setInterval(() => {
      elapsed += 120
      const nextProgress = Math.min(96, 12 + (elapsed / totalDuration) * 82)
      setLoadingProgress(nextProgress)
    }, 120)

    return () => window.clearInterval(interval)
  }, [mode])

  // Get the current persona's display info
  const currentPersonaInfo = useMemo(() => {
    for (const group of personaGroups) {
      const persona = group.personas.find(p => p.id === selectedPersona)
      if (persona) {
        return { label: persona.label, group: group.group_label, description: persona.description }
      }
    }
    // Fallback: format the persona_mode nicely
    const raw = selectedPersona.replace(/_/g, ' ')
    return {
      label: raw.split(' ').map(word => word.charAt(0).toUpperCase() + word.slice(1)).join(' '),
      group: '',
      description: '',
    }
  }, [selectedPersona, personaGroups])

  const handlePersonaChange = (newPersonaId: string) => {
    setSelectedPersona(newPersonaId)
    setShowPersonaSelector(false)
  }

  const handleConfirmClick = () => {
    if (!plan || !onConfirm) return
    // Pass the updated persona if it changed
    const updatedPersona = selectedPersona !== plan.inputs.persona_mode ? selectedPersona : undefined
    onConfirm(updatedPersona)
  }

  const updatePlanSummary = (value: string) => {
    if (!plan || !onPlanChange) return
    onPlanChange({ ...plan, plan_summary: value })
  }

  const updateQuestion = (key: QuestionKey, index: number, value: string) => {
    if (!plan || !onPlanChange) return
    const next = { ...plan.research_questions }
    const arr = [...next[key]]
    arr[index] = value
    next[key] = arr
    onPlanChange({ ...plan, research_questions: next })
  }

  const addQuestion = (key: QuestionKey) => {
    if (!plan || !onPlanChange) return
    const next = { ...plan.research_questions }
    next[key] = key === 'decision_questions' ? ['', ...next[key]] : [...next[key], '']
    onPlanChange({ ...plan, research_questions: next })
  }

  const removeQuestion = (key: QuestionKey, index: number) => {
    if (!plan || !onPlanChange) return
    const next = { ...plan.research_questions }
    next[key] = next[key].filter((_, i) => i !== index)
    onPlanChange({ ...plan, research_questions: next })
  }

  if (mode === 'loading') {
    return (
      <div className="dark fixed inset-0 z-[120] bg-[#09090B] text-white" data-testid="mobile-plan-loading">
        <div
          className="flex h-full flex-col px-4"
          style={{
          paddingTop: 'max(env(safe-area-inset-top), 0.75rem)',
          paddingBottom: 'max(env(safe-area-inset-bottom), 0.875rem)',
          }}
        >
          <header className="flex items-center justify-between border-b border-white/10 pb-3">
            <div className="w-9" />
            <div className="text-center">
              <p className="text-[11px] uppercase tracking-[0.35em] text-white/35">Research plan</p>
              <h2 className="mt-1.5 text-[1.05rem] font-semibold tracking-[-0.02em] text-white">Designing your mission</h2>
            </div>
            <button
              type="button"
              onClick={onCancel}
              className="flex h-9 w-9 items-center justify-center rounded-full border border-white/10 bg-white/[0.04] text-white/70 transition hover:bg-white/[0.08] hover:text-white"
              aria-label="Cancel plan generation"
            >
              <IconX className="h-4 w-4" />
            </button>
          </header>

          <div className="flex-1 overflow-y-auto">
            <div className="mx-auto flex max-w-md flex-col justify-center gap-4 py-6">
              <section className="rounded-[28px] border border-white/10 bg-gradient-to-b from-[#111827] via-[#0F172A] to-[#09090B] p-5 shadow-[0_20px_60px_rgba(0,0,0,0.34)]">
                <div className="flex items-center gap-4">
                  <div className="flex h-12 w-12 items-center justify-center rounded-full bg-white/[0.08] text-sky-300 shadow-[0_0_30px_rgba(56,189,248,0.16)]">
                    <IconSparkles className="h-6 w-6" />
                  </div>
                  <div>
                    <p className="text-xs uppercase tracking-[0.35em] text-white/35">Generating</p>
                    <h3
                      className="mt-2 font-semibold tracking-[-0.03em] text-white"
                      style={{ fontSize: 'clamp(1.4rem, 5.4vw, 1.9rem)', lineHeight: '1.1' }}
                    >
                      Your research plan
                    </h3>
                  </div>
                </div>
                <p className="mt-4 text-sm leading-6 text-white/65">
                  We are analyzing your prompt, selecting the right persona, and building a tailored mission before the full research run begins.
                </p>
              </section>

              {query && (
                <section className="rounded-[24px] border border-white/10 bg-white/[0.03] p-3.5">
                  <p className="text-[11px] uppercase tracking-[0.35em] text-white/35">Mission prompt</p>
                  <p
                    className="mt-2.5 font-medium text-white"
                    style={{ fontSize: 'clamp(1rem, 4.2vw, 1.2rem)', lineHeight: '1.8' }}
                  >
                    "{query}"
                  </p>
                </section>
              )}

              <section className="rounded-[24px] border border-white/10 bg-white/[0.03] p-3.5">
                <div className="flex items-center justify-between">
                  <p className="text-sm font-medium text-white/80">Progress</p>
                  <p className="text-sm text-white/45">{Math.round(loadingProgress)}%</p>
                </div>
                <div className="mt-3 h-2 overflow-hidden rounded-full bg-white/10">
                  <div
                    className="h-full rounded-full bg-gradient-to-r from-sky-400 via-blue-400 to-cyan-300 transition-all duration-300"
                    style={{ width: `${loadingProgress}%` }}
                  />
                </div>
                <p className="mt-3 text-sm leading-6 text-white/50">
                  This usually takes a moment. Once the plan is ready, the full review screen will open automatically.
                </p>
              </section>
            </div>
          </div>

          <div className="pt-3">
            <button
              type="button"
              onClick={onCancel}
              className="w-full rounded-full border border-white/15 bg-white/[0.04] px-5 py-3 text-sm font-semibold text-white transition hover:bg-white/[0.08]"
            >
              Cancel
            </button>
          </div>
        </div>
      </div>
    )
  }

  if (!plan) return null

  return (
    <div className="dark fixed inset-0 z-[120] bg-[#09090B] text-white" data-testid="mobile-plan-review-screen">
      <div
        className="flex h-full flex-col"
        style={{
          paddingTop: 'max(env(safe-area-inset-top), 0px)',
          paddingBottom: 'max(env(safe-area-inset-bottom), 0px)',
        }}
      >
        <header
          className="sticky top-0 z-10 border-b border-white/10 bg-[#09090B]/95 px-4 backdrop-blur-xl"
          style={{
            paddingTop: 'max(env(safe-area-inset-top), 0.75rem)',
            paddingBottom: '0.75rem',
          }}
        >
          <div className="flex items-center justify-between">
            <button
              onClick={onCancel}
              className="flex h-9 w-9 items-center justify-center rounded-full border border-white/10 bg-white/[0.04] text-white/70 transition hover:bg-white/[0.08] hover:text-white"
              aria-label="Close"
            >
              <IconX className="h-4 w-4" />
            </button>
            <div className="text-center">
              <h2 className="text-[1.05rem] font-semibold tracking-[-0.02em] text-white">Review & customize</h2>
            </div>
            <div className="w-9" />
          </div>
        </header>

        <div ref={scrollRef} className="flex-1 overflow-y-auto px-4 py-4">
          <div className="mx-auto max-w-md space-y-4" data-testid="plan-review-content">
            <section className="rounded-[28px] border border-white/10 bg-white/[0.04] p-4 shadow-[0_18px_54px_rgba(0,0,0,0.28)]">
              <div className="flex items-center justify-between gap-3">
                <p className="text-sm font-semibold uppercase tracking-[0.28em] text-white/70">Mission</p>
                <div className="inline-flex items-center gap-1.5 rounded-full border border-emerald-400/20 bg-emerald-400/10 px-3 py-1 text-[11px] uppercase tracking-[0.3em] text-emerald-200">
                  <IconPencil className="h-3.5 w-3.5" />
                  <span>Editable</span>
                </div>
              </div>
              <textarea
                value={plan.plan_summary}
                onChange={e => updatePlanSummary(e.target.value)}
                className="mt-3 w-full resize-none border-none bg-transparent p-0 font-semibold tracking-[-0.04em] text-white placeholder:text-white/25 focus:outline-none focus:ring-0"
                style={{
                  fontSize: 'clamp(1.12rem, 4.4vw, 1.55rem)',
                  lineHeight: '1.24',
                  minHeight: 'clamp(12rem, 34vh, 15.5rem)',
                }}
                placeholder="Summarize how this intelligence package will answer the prompt..."
                rows={7}
              />
            </section>

            <section className="rounded-[24px] border border-white/10 bg-white/[0.03] p-3.5">
              <div className="flex items-center justify-between gap-4">
                <p className="text-[11px] uppercase tracking-[0.35em] text-white/35">Persona lens</p>
                <div className="rounded-full border border-emerald-400/20 bg-emerald-400/10 px-3 py-1 text-[11px] uppercase tracking-[0.3em] text-emerald-200">
                  Editable
                </div>
              </div>
              <button
                onClick={() => setShowPersonaSelector(!showPersonaSelector)}
                className="mt-2.5 inline-flex items-center gap-2 text-left font-medium text-white transition hover:text-sky-200"
                style={{ fontSize: 'clamp(0.95rem, 4vw, 1.05rem)' }}
              >
                <span>{currentPersonaInfo.label}{selectedPersona !== plan.inputs.persona_mode ? ' (changed)' : ''}</span>
                <IconChevron className="h-4 w-4 text-white/45" expanded={showPersonaSelector} />
              </button>
              <p className="mt-1.5 whitespace-nowrap text-sm leading-6 text-white/55">
                Scenario axis: <span className="text-white/80">{plan.scenario_plan.axis_label}</span>
              </p>

              {showPersonaSelector && (
                <div className="mt-4 overflow-hidden rounded-3xl border border-white/10 bg-black/30">
                  {personaGroups.map(group => (
                    <div key={group.group_id}>
                      <div className="sticky top-0 bg-white/[0.04] px-4 py-2 text-[11px] uppercase tracking-[0.35em] text-white/35">
                        {group.group_label}
                      </div>
                      {group.personas.map(persona => (
                        <button
                          key={persona.id}
                          onClick={() => handlePersonaChange(persona.id)}
                          className={`w-full border-t border-white/5 px-4 py-3 text-left transition hover:bg-white/[0.05] ${
                            selectedPersona === persona.id ? 'bg-white/[0.07]' : ''
                          }`}
                        >
                          <div className="font-medium text-white">
                            {persona.label}
                            {selectedPersona === persona.id && <span className="ml-2 text-emerald-300">✓</span>}
                          </div>
                          {persona.description && (
                            <div className="mt-1 text-xs leading-5 text-white/45">{persona.description}</div>
                          )}
                        </button>
                      ))}
                    </div>
                  ))}
                </div>
              )}
            </section>

            <button
              onClick={() => setShowAdvanced(!showAdvanced)}
              className="flex w-full items-center justify-center gap-2 rounded-2xl border border-white/10 bg-white/[0.03] px-4 py-2.5 text-sm text-white/75 transition hover:bg-white/[0.06] hover:text-white"
            >
              <span>{showAdvanced ? 'Hide editable prompts' : 'Review & Edit Research Prompts'}</span>
              <IconChevron className="h-4 w-4" expanded={showAdvanced} />
            </button>

            {showAdvanced && (
              <div className="space-y-4">
                {Object.keys(QUESTION_LABELS).map(key => {
                  const typedKey = key as QuestionKey
                  const questions = plan.research_questions[typedKey]
                  return (
                    <section key={typedKey} className="rounded-[24px] border border-white/10 bg-white/[0.03] p-3.5">
                      <div className="mb-3 flex items-center justify-between">
                        <div>
                          <p className="text-[11px] uppercase tracking-[0.35em] text-white/35">{questions.length} prompts</p>
                          <h4 className="text-base font-semibold text-white">{QUESTION_LABELS[typedKey]}</h4>
                        </div>
                        <button
                          onClick={() => addQuestion(typedKey)}
                          className="text-[11px] uppercase tracking-[0.35em] text-white/55 transition hover:text-white"
                        >
                          Add item
                        </button>
                      </div>
                      <div className="space-y-2.5">
                        {questions.map((question, index) => (
                          <div key={`${typedKey}-${index}`} className="group relative">
                            <textarea
                              value={question}
                              onChange={e => updateQuestion(typedKey, index, e.target.value)}
                              className="w-full rounded-2xl border border-white/10 bg-black/30 p-3.5 pr-11 text-sm leading-6 text-white placeholder:text-white/25 focus:outline-none focus:ring-2 focus:ring-sky-400/30"
                              placeholder="Describe the question or validation step..."
                              rows={2}
                            />
                            {questions.length > 1 && (
                              <button
                                onClick={() => removeQuestion(typedKey, index)}
                                className="absolute right-3 top-3 text-white/35 transition hover:text-rose-300"
                                aria-label="Remove question"
                              >
                                <IconX className="h-4 w-4" />
                              </button>
                            )}
                          </div>
                        ))}
                      </div>
                    </section>
                  )
                })}
              </div>
            )}
          </div>
        </div>

        <footer
          className="border-t border-white/10 bg-[#09090B]/95 px-4 pt-3 backdrop-blur-xl"
          style={{ paddingBottom: 'max(env(safe-area-inset-bottom), 0.875rem)' }}
        >
          <div className="mx-auto max-w-md">
            <div className="grid grid-cols-2 gap-2.5">
              <button
                type="button"
                onClick={onCancel}
                className="rounded-full border border-white/15 bg-white/[0.04] px-4 py-2.5 text-sm font-medium text-white transition hover:bg-white/[0.08]"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={handleConfirmClick}
                disabled={isConfirming}
                className="rounded-full bg-emerald-500 px-4 py-2.5 text-sm font-semibold text-white transition hover:bg-emerald-400 disabled:cursor-not-allowed disabled:opacity-60"
              >
                {isConfirming ? 'Approving…' : 'Approve & start'}
              </button>
            </div>
          </div>
        </footer>
      </div>
    </div>
  )
}
